import { Field, Form, Formik, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Badge,
  Button,
  Heading,
  RadioGroup,
  Span,
} from "@icg360/design-system";

import { MSSLink } from "components/common/link";
import { MSSDrawer } from "components/mss-drawer";
import { InputField } from "components/shared/form-fields";
import { addPolicySchema } from "consts";
import {
  useKeystone,
  usePoliciesList,
  usePolicyPlatformId,
  useUser,
} from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import CheckIcon from "images/icon-check.svg?react";
import { capitalizeEachWord, switchPolicy, trackEvent } from "utils";
import { getPolicyTypeLabel } from "utils/policy-type-labels";

import styles from "./policy-drawer.module.scss";

type PolicyDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

type AddPolicyFormSchema = {
  policyNumber: string;
  zipCode: string;
};

type DrawerView = "policyList" | "addPolicy" | "success";

const getPolicyStatus = (policyState?: string | null) => {
  if (!policyState) return "Unknown";
  const statusMap: { [key: string]: string } = {
    ACTIVEPOLICY: "Active",
    CANCELLEDPOLICY: "Cancelled",
    NONRENEWEDPOLICY: "Non-renewed",
  };
  return statusMap[policyState] ?? "Unknown";
};

// Drawer manages three views: policyList (default), addPolicy (form), and success (confirmation)
export const PolicyDrawer = ({ isOpen, onClose }: PolicyDrawerProps) => {
  const selectedPolicyId = usePolicyPlatformId();
  const { data, mutate: refetchPolicies } = usePoliciesList();
  const [selectedPolicyIdForSwitch, setSelectedPolicyIdForSwitch] = useState<
    string | null
  >(null);
  const [view, setView] = useState<DrawerView>("policyList");
  const [addedPolicyNumber, setAddedPolicyNumber] = useState<string | null>(
    null
  );

  const keystone = useKeystone();
  const user = useUser();
  const navigate = useNavigate();

  // Reset drawer state when opening
  useEffect(() => {
    if (isOpen && selectedPolicyId) {
      setSelectedPolicyIdForSwitch(selectedPolicyId);
      setView("policyList");
      setAddedPolicyNumber(null);
      trackEvent("policySwitcher_opened");
    }
  }, [isOpen, selectedPolicyId]);

  // Sync selected policy from context, but don't override if user is viewing success state
  useEffect(() => {
    if (isOpen && view !== "success" && selectedPolicyId) {
      setSelectedPolicyIdForSwitch(selectedPolicyId);
    }
  }, [selectedPolicyId, isOpen, view]);

  // When a new policy is added, update the selection to the newly added policy
  useEffect(() => {
    if (addedPolicyNumber && data) {
      const newlyAddedPolicy = data.find(
        (p) => p.platformPolicyId === addedPolicyNumber
      );
      if (newlyAddedPolicy) {
        setSelectedPolicyIdForSwitch(newlyAddedPolicy.platformPolicyId);
      }
    }
  }, [addedPolicyNumber, data]);

  const handleViewPolicy = () => {
    // First try to find by selectedPolicyIdForSwitch
    let policyToSwitch = data?.find(
      (p) => p.platformPolicyId === selectedPolicyIdForSwitch
    );

    // If not found and we have an addedPolicyNumber, try finding by that
    if (!policyToSwitch && addedPolicyNumber) {
      policyToSwitch = data?.find(
        (p) => p.platformPolicyId === addedPolicyNumber
      );
    }

    if (policyToSwitch?.keystonePolicyId) {
      switchPolicy(policyToSwitch.keystonePolicyId);
      onClose();
    }
  };

  const handleAddPolicySubmit = async (
    { policyNumber, zipCode }: AddPolicyFormSchema,
    { resetForm, setStatus, setSubmitting }
  ) => {
    trackEvent("Add Policy Button Clicked");
    if (!user) return;
    const res = await keystone.linkPolicy(user.email, policyNumber, zipCode);

    if (isError(res)) {
      trackEvent("Error Displayed (Add Policy)", {
        errorMessage: res.message,
      });
      setStatus(res.message);
      setSubmitting(false);
      return;
    }

    trackEvent("Policy Added", {
      policyNumber: policyNumber,
      zipCode: zipCode,
      addPolicyOrigin: "Direct",
    });

    // Show success state instead of closing drawer - user must explicitly choose to view policy or close
    refetchPolicies();
    resetForm();
    setSubmitting(false);
    setAddedPolicyNumber(policyNumber);
    setView("success");
  };

  const handleBackToPolicyList = () => {
    setView("policyList");
  };

  const handleViewAddedPolicy = () => {
    // Close drawer before navigation to prevent mobile drawer from blocking page reload
    // switchPolicy() triggers window.location.reload(), so drawer must be dismissed first
    if (addedPolicyNumber && data) {
      const newlyAddedPolicy = data.find(
        (p) => p.platformPolicyId === addedPolicyNumber
      );
      if (newlyAddedPolicy?.keystonePolicyId) {
        onClose();
        setTimeout(() => {
          switchPolicy(newlyAddedPolicy.keystonePolicyId);
        }, 100);
        return;
      }
    }
    handleViewPolicy();
  };

  const handleCloseSuccess = () => {
    onClose();
  };

  const handleAddPolicyClick = () => {
    trackEvent("policySwitcher_addPolicyClicked");
    trackEvent("Add Policy Started");
    setView("addPolicy");
  };

  const renderPolicyList = () => (
    <>
      <RadioGroup
        value={selectedPolicyIdForSwitch ?? selectedPolicyId}
        onChange={(value) => {
          const policyId = value as string;
          setSelectedPolicyIdForSwitch(policyId);
          trackEvent("policySwitcher_policySelected", { policyId });
        }}
        className={styles.policyRadioGroup}
      >
        {data?.map((policy) => {
          const address = capitalizeEachWord(
            policy.propertyAddress?.street1 ?? ""
          );
          const policyStatus = getPolicyStatus(policy.policyState);
          const isActive = policyStatus === "Active";

          const policyTypeLabel = getPolicyTypeLabel(policy.policyType);
          const policyDescription = policyTypeLabel
            ? `${policyTypeLabel} #${policy.platformPolicyId}`
            : `Policy #${policy.platformPolicyId}`;

          // Determine icon based on policy type
          const iconName = policy.isCommercial
            ? "Store"
            : policy.policyType === "HO6"
            ? "MixedUse"
            : "Home";

          return (
            <RadioGroup.Card
              key={policy.platformPolicyId}
              value={policy.platformPolicyId}
              title={
                <div className={styles.policyTitle}>
                  <Span size="md" className={styles.policyAddress}>
                    {address}
                  </Span>
                  <Badge appearance={isActive ? "success" : "neutral"}>
                    {policyStatus}
                  </Badge>
                </div>
              }
              description={
                <Span size="sm" className={styles.policyDescription}>
                  {policyDescription}
                </Span>
              }
              uiIcon={iconName}
            />
          );
        })}
      </RadioGroup>
      <div className={styles.addPolicyButton}>
        <Button
          appearance="secondary"
          leadingIcon="AddCircle"
          onPress={handleAddPolicyClick}
          fluid
        >
          Add another policy
        </Button>
      </div>
    </>
  );

  const drawerTitle =
    view === "addPolicy" || view === "success"
      ? "Add a policy"
      : "View a different policy";

  const AddPolicyBodyAndFooter = () => {
    const {
      isValid,
      isSubmitting,
      dirty,
      resetForm,
      status,
      submitForm,
      values,
    } = useFormikContext<AddPolicyFormSchema>();

    const isFormEmpty = !values.policyNumber && !values.zipCode && !dirty;

    const onContactRepClick = () => {
      trackEvent("addPolicy_contactAgent");
    };

    return (
      <>
        <MSSDrawer.Body>
          <div className={styles.drawerContent}>
            <div className={styles.addPolicyContent}>
              <div className={styles.addPolicyDescription}>
                You can find your policy number in your policy declaration page.
                To quote a new policy,{" "}
                <MSSLink
                  to="/my/support"
                  onClick={(e) => {
                    e.preventDefault();
                    onContactRepClick();
                    onClose();
                    navigate("/my/support", {
                      state: { scrollToContactRepresentative: true },
                    });
                  }}
                >
                  contact your insurance representative
                </MSSLink>
                .
              </div>
              <Form className={styles.addPolicyForm}>
                <Field
                  title="Policy number"
                  name="policyNumber"
                  component={InputField}
                />

                <Field
                  title="Property ZIP Code"
                  name="zipCode"
                  component={InputField}
                  helpMessage="If your policy covers more than one location, enter the ZIP Code of the first or primary location."
                />
              </Form>
              {status ? (
                <Alert appearance="danger" title="" description={status} />
              ) : null}
            </div>
          </div>
        </MSSDrawer.Body>
        <MSSDrawer.Footer
          primaryLabel="Add policy"
          onPrimary={submitForm}
          primaryDisabled={!isValid || isSubmitting || isFormEmpty}
          primaryLoading={isSubmitting}
          primaryButtonTestId="switcher-add-policy"
          secondaryLabel="Cancel"
          onSecondary={() => {
            handleBackToPolicyList();
            resetForm();
          }}
          secondaryButtonTestId="switcher-add-policy-cancel"
        />
      </>
    );
  };

  return (
    <MSSDrawer isOpen={isOpen} onClose={onClose}>
      <MSSDrawer.Header
        title={drawerTitle}
        onClose={onClose}
        closeButtonTestId="switcher-close"
      />
      {view === "policyList" ? (
        <>
          <MSSDrawer.Body>
            <div className={styles.drawerContent}>{renderPolicyList()}</div>
          </MSSDrawer.Body>
          <MSSDrawer.Footer
            primaryLabel="View policy"
            onPrimary={handleViewPolicy}
            primaryButtonTestId="switcher-view-policy"
            secondaryLabel="Cancel"
            onSecondary={onClose}
            secondaryButtonTestId="switcher-cancel"
          />
        </>
      ) : view === "success" ? (
        <>
          <MSSDrawer.Body>
            <div className={styles.drawerContent}>
              <div className={styles.successContent}>
                <div className={styles.successIcon}>
                  <CheckIcon className={styles.checkImage} />
                </div>
                <Heading size="md" tag="h3">
                  Policy added!
                </Heading>
                <Span size="sm" className={styles.successMessage}>
                  Your policy <strong>{addedPolicyNumber}</strong> has been
                  successfully added to your account.
                </Span>
              </div>
            </div>
          </MSSDrawer.Body>
          <MSSDrawer.Footer
            primaryLabel="View policy"
            onPrimary={handleViewAddedPolicy}
            primaryButtonTestId="switcher-view-policy"
            secondaryLabel="Close"
            onSecondary={handleCloseSuccess}
            secondaryButtonTestId="switcher-close-footer"
          />
        </>
      ) : (
        <Formik
          initialValues={{ policyNumber: "", zipCode: "" }}
          onSubmit={handleAddPolicySubmit}
          validationSchema={addPolicySchema}
          enableReinitialize
        >
          <AddPolicyBodyAndFooter />
        </Formik>
      )}
    </MSSDrawer>
  );
};
